// Right-hand rail: how far the three steps have got, a summary of the selection
// and the mapping progress bar, plus the static tips card.

import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { Flex } from '@decisiv/ui-components';
import { Trans } from '@lingui/macro';
import CheckCircleF from '@decisiv/iconix/lib/components/CheckCircleF';

import {
  CheckDot,
  ChecklistRow,
  ProgressFill,
  ProgressTrack,
  RailCard,
  RailRowSub,
  RailRowTitle,
  RailSectionTitle,
  RailSidebar,
  RailTitle,
  SummaryRow,
  TipsList,
} from '../style';

const CompletionRail = ({
  name,
  nameOk,
  integOk,
  selectedCount,
  mappedCount,
  totalMappings,
  mappingsComplete,
  mappedPct,
}) => (
  <RailSidebar>
    <RailCard>
      <RailTitle>
        <Trans>Completion</Trans>
      </RailTitle>

      <ChecklistRow>
        <CheckDot done={nameOk}>
          {nameOk && <CheckCircleF size="small" />}
        </CheckDot>
        <div>
          <RailRowTitle>
            <Trans>Name your shared setting</Trans>
          </RailRowTitle>
          <RailRowSub>
            {nameOk ? (
              <em>&ldquo;{name.trim()}&rdquo;</em>
            ) : (
              <Trans>At least 3 characters</Trans>
            )}
          </RailRowSub>
        </div>
      </ChecklistRow>
      <ChecklistRow>
        <CheckDot done={integOk} warn={selectedCount === 1} />
        <div>
          <RailRowTitle>
            <Trans>Pick 2+ integrations</Trans>
          </RailRowTitle>
          <RailRowSub>
            {selectedCount} <Trans>selected</Trans>
          </RailRowSub>
        </div>
      </ChecklistRow>
      <ChecklistRow>
        <CheckDot
          done={mappingsComplete}
          warn={mappedCount > 0 && mappedCount < totalMappings}
        >
          {mappingsComplete && <CheckCircleF size="small" />}
        </CheckDot>
        <div>
          <RailRowTitle>
            <Trans>Map attributes</Trans>
          </RailRowTitle>
          <RailRowSub>
            {totalMappings === 0 ? (
              <Trans>No mappings required</Trans>
            ) : (
              <>
                {mappedCount} <Trans>of</Trans> {totalMappings}{' '}
                <Trans>cells mapped</Trans>
              </>
            )}
          </RailRowSub>
        </div>
      </ChecklistRow>

      <RailSectionTitle>
        <Trans>Summary</Trans>
      </RailSectionTitle>
      <SummaryRow>
        <span>
          <Trans>Step</Trans>
        </span>
        <strong>
          {(nameOk ? 1 : 0) + (integOk ? 1 : 0) + (mappingsComplete ? 1 : 0)} of
          3
        </strong>
      </SummaryRow>
      <SummaryRow>
        <span>
          <Trans>Integrations</Trans>
        </span>
        <strong>{selectedCount}</strong>
      </SummaryRow>
      <SummaryRow>
        <span>
          <Trans>Mappings</Trans>
        </span>
        <strong>
          {totalMappings === 0 ? (
            <Trans>None required</Trans>
          ) : (
            `${mappedCount} / ${totalMappings}`
          )}
        </strong>
      </SummaryRow>

      <ProgressTrack>
        <ProgressFill pct={mappedPct} />
      </ProgressTrack>
      <Flex justifyContent="space-between">
        <RailRowSub>
          {mappedPct}% <Trans>mapped</Trans>
        </RailRowSub>
        <RailRowSub>
          {totalMappings - mappedCount} <Trans>remaining</Trans>
        </RailRowSub>
      </Flex>
    </RailCard>

    <RailCard>
      <RailTitle>
        <Trans>Tips</Trans>
      </RailTitle>
      <TipsList>
        <li>
          <Trans>
            Leave a cell blank to skip &mdash; values flow only where mapped.
          </Trans>
        </li>
        <li>
          <Trans>
            <strong>Attributes/Lifecycle</strong> integrations sync both
            attribute values and location lifecycle. <strong>Lifecycle</strong>{' '}
            ones have no location-level attributes, so they only sync lifecycle.
          </Trans>
        </li>
      </TipsList>
    </RailCard>
  </RailSidebar>
);

CompletionRail.propTypes = {
  name: PropTypes.string.isRequired,
  nameOk: PropTypes.bool.isRequired,
  integOk: PropTypes.bool.isRequired,
  selectedCount: PropTypes.number.isRequired,
  mappedCount: PropTypes.number.isRequired,
  totalMappings: PropTypes.number.isRequired,
  mappingsComplete: PropTypes.bool.isRequired,
  mappedPct: PropTypes.number.isRequired,
};

export default memo(CompletionRail);
